import { useEffect, useMemo } from "react";
import { ResponsiveContainer, Tooltip, Treemap } from "recharts";
import { formatCompact, formatNumber } from "@/lib/formatters";
import type { OiTreemapNode } from "@/types/treemap";
import { getOiTreemapNodeByPath, resolveOiTreemapPath } from "@/lib/oi-treemap";

type OiCategoryTreemapProps = {
  root: OiTreemapNode;
  path: string[];
  onPathChange: (path: string[]) => void;
  maxDepth?: number;
};

type TreemapContentProps = {
  depth?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  payload?: OiTreemapNode;
  value?: number;
  fill?: string;
  children?: unknown;
  meta?: OiTreemapNode["meta"];
  onNodeClick: (node: OiTreemapNode) => void;
};

function extractNodeFromTreemapProps(
  props: Omit<TreemapContentProps, "onNodeClick">
): OiTreemapNode | null {
  const payloadNode = props.payload as OiTreemapNode | undefined;
  const name =
    typeof props.name === "string"
      ? props.name
      : typeof payloadNode?.name === "string"
        ? payloadNode.name
        : "";
  if (!name) return null;

  const value = Number(props.value ?? payloadNode?.value ?? 0);

  return {
    name,
    value: Number.isFinite(value) ? value : 0,
    children: Array.isArray(props.children)
      ? (props.children as OiTreemapNode[])
      : Array.isArray(payloadNode?.children)
        ? (payloadNode.children as OiTreemapNode[])
      : undefined,
    meta: payloadNode?.meta,
    fill:
      typeof props.fill === "string"
        ? props.fill
        : typeof payloadNode?.fill === "string"
          ? payloadNode.fill
          : undefined,
  };
}

function TreemapCell({
  depth,
  x,
  y,
  width,
  height,
  name,
  payload,
  value,
  onNodeClick,
  fill,
  children,
  meta,
}: TreemapContentProps) {
  if (
    depth == null ||
    x == null ||
    y == null ||
    width == null ||
    height == null ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  const node = extractNodeFromTreemapProps({
    depth,
    x,
    y,
    width,
    height,
    name,
    payload,
    value,
    fill,
    children,
    meta,
  });
  if (!node) return null;

  if (depth === 0) {
    return null;
  }
  if (depth !== 1) {
    return null;
  }

  const isGrouped = Boolean(node.meta?.isGrouped);
  const isClickable = Boolean(node.children?.length);
  const cellFill = node.fill ?? "#dff3ff";

  const showLabel = width > 96 && height > 30;
  const showValue = width > 120 && height > 58;
  const labelText = node.name.length > 34 ? `${node.name.slice(0, 31)}...` : node.name;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={cellFill}
        fillOpacity={0.96}
        stroke="var(--border)"
        strokeWidth={1}
        style={{ cursor: isClickable ? "pointer" : "default" }}
        onClick={() => {
          if (isClickable) {
            onNodeClick(node);
          }
        }}
      />

      {showLabel ? (
        <>
          <text
            x={x + 10}
            y={y + 22}
            fill="#0b1b34"
            fontSize={13}
            fontWeight={isGrouped ? 750 : 700}
            stroke="rgba(255,255,255,0.55)"
            strokeWidth={2}
            paintOrder="stroke"
          >
            {labelText}
          </text>
        </>
      ) : null}

      {showValue ? (
        <text
          x={x + 10}
          y={y + 40}
          fill="#102747"
          fontSize={12}
          fontWeight={700}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth={1.75}
          paintOrder="stroke"
        >
          {formatCompact(node.value, "$")}
        </text>
      ) : null}
    </g>
  );
}

function OiTreemapTooltip({
  active,
  payload,
  rootValue,
}: {
  active?: boolean;
  payload?: Array<{ payload?: OiTreemapNode }>;
  rootValue: number;
}) {
  if (!active || !payload?.length) return null;

  const node = payload[0]?.payload;
  if (!node) return null;

  const percent = rootValue > 0 ? (node.value / rootValue) * 100 : 0;

  return (
    <div className="min-w-[190px] border border-border/80 bg-background/95 px-2.5 py-2 shadow-lg backdrop-blur-sm">
      <div className="text-xs font-semibold text-foreground">{node.name}</div>
      <div className="mt-1 space-y-1 text-xs text-muted-foreground">
        <div className="flex items-center justify-between gap-2">
          <span>Open Interest</span>
          <span className="font-mono text-foreground">{formatCompact(node.value, "$")}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>Share of view</span>
          <span className="font-mono text-foreground">{formatNumber(percent, 2)}%</span>
        </div>
      </div>
    </div>
  );
}

export function OiCategoryTreemap({
  root,
  path,
  onPathChange,
  maxDepth = 3,
}: OiCategoryTreemapProps) {
  const resolvedPath = useMemo(
    () => resolveOiTreemapPath(root, path, maxDepth),
    [root, path, maxDepth]
  );

  useEffect(() => {
    if (
      resolvedPath.length !== path.length ||
      resolvedPath.some((segment, index) => segment !== path[index])
    ) {
      onPathChange(resolvedPath);
    }
  }, [onPathChange, path, resolvedPath]);

  const currentNode = useMemo(
    () => getOiTreemapNodeByPath(root, resolvedPath, maxDepth),
    [root, resolvedPath, maxDepth]
  );

  const breadcrumbs = [root.name, ...resolvedPath];

  const handleNodeClick = (node: OiTreemapNode) => {
    if (!node.children?.length) return;
    if (resolvedPath.length >= maxDepth) return;
    onPathChange([...resolvedPath, node.name]);
  };

  const treemapData = currentNode.children ?? [];
  const hasChildren = treemapData.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 text-xs font-mono">
        {breadcrumbs.map((segment, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <button
              key={`${segment}-${index}`}
              type="button"
              className={`rounded-sm px-1.5 py-0.5 transition ${
                isLast ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-primary/10 hover:text-foreground"
              }`}
              onClick={() => {
                if (!isLast) {
                  onPathChange(resolvedPath.slice(0, index));
                }
              }}
              disabled={isLast}
            >
              {segment}
            </button>
          );
        })}
      </div>

      {hasChildren ? (
        <div className="h-[540px] min-h-[540px] min-w-0 w-full border border-border/70 bg-background/40">
          <ResponsiveContainer
            width="100%"
            height="100%"
            minWidth={0}
            minHeight={0}
            initialDimension={{ width: 800, height: 540 }}
          >
            <Treemap
              data={treemapData}
              dataKey="value"
              isAnimationActive={false}
              stroke="var(--border)"
              onClick={(node) => {
                const extracted = extractNodeFromTreemapProps(node as unknown as Omit<TreemapContentProps, "onNodeClick">);
                if (extracted) {
                  handleNodeClick(extracted);
                }
              }}
              content={(props) => (
                <TreemapCell
                  {...(props as Omit<TreemapContentProps, "onNodeClick">)}
                  onNodeClick={handleNodeClick}
                />
              )}
            >
              <Tooltip
                content={(tooltipProps) => (
                  <OiTreemapTooltip
                    active={tooltipProps.active}
                    payload={tooltipProps.payload as Array<{ payload?: OiTreemapNode }>}
                    rootValue={currentNode.value}
                  />
                )}
              />
            </Treemap>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-[300px] w-full items-center justify-center border border-border/70 bg-background/45 px-4 text-center">
          <div>
            <p className="text-sm font-medium">No deeper breakdown available</p>
            <p className="mt-1 text-xs text-muted-foreground">Select a breadcrumb to navigate up.</p>
          </div>
        </div>
      )}
    </div>
  );
}
